import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import { toast } from "react-toastify";
import { useDropzone } from "react-dropzone";
import Papa from "papaparse";
import * as XLSX from "xlsx";

const Blacklist = () => {
    const [items, setItems] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const [newDomain, setNewDomain] = useState("");
    const [searchDomain, setSearchDomain] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editingDomain, setEditingDomain] = useState("");

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [uploadRows, setUploadRows] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const loadItems = async () => {
        setIsLoading(true);
        try {
            const resp = await axiosInstance.get("/blacklist/blacklists");
            setItems(resp.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to load blacklist.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadItems();
    }, []);

    const filteredItems = useMemo(() => {
        const keyword = searchDomain.trim().toLowerCase();
        if (!keyword) return items;
        return items.filter((item) => String(item?.domain || "").toLowerCase().includes(keyword));
    }, [items, searchDomain]);

    const rowCountLabel = useMemo(() => {
        const count = filteredItems.length;
        return `${count} domain${count === 1 ? "" : "s"}`;
    }, [filteredItems.length]);

    const handleCreate = async () => {
        const domain = newDomain.trim();
        if (!domain) {
            toast.warn("Please input a domain.");
            return;
        }

        setIsSaving(true);
        try {
            await axiosInstance.post("/blacklist/blacklists", { domain });
            setNewDomain("");
            toast.success("Blacklist item created.");
            loadItems();
        } catch (err) {
            toast.error(err.response?.data?.error || "Create failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const startEdit = (item) => {
        setEditingId(item.blacklist_id);
        setEditingDomain(item.domain || "");
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditingDomain("");
    };

    const handleUpdate = async () => {
        if (!editingId) return;

        const domain = editingDomain.trim();
        if (!domain) {
            toast.warn("Please input a domain.");
            return;
        }

        setIsSaving(true);
        try {
            await axiosInstance.put(`/blacklist/blacklists/${editingId}`, { domain });
            toast.success("Blacklist item updated.");
            cancelEdit();
            loadItems();
        } catch (err) {
            toast.error(err.response?.data?.error || "Update failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const openDeleteDialog = (item) => {
        setDeleteTarget(item);
    };

    const closeDeleteDialog = () => {
        if (isSaving) return;
        setDeleteTarget(null);
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;

        setIsSaving(true);
        try {
            await axiosInstance.delete(`/blacklist/blacklists/${deleteTarget.blacklist_id}`);
            toast.success("Blacklist item deleted.");

            if (editingId === deleteTarget.blacklist_id) {
                cancelEdit();
            }

            setDeleteTarget(null);
            loadItems();
        } catch (err) {
            toast.error(err.response?.data?.error || "Delete failed.");
        } finally {
            setIsSaving(false);
        }
    };

    const formatDate = (date) => {
        if (!date) return "";

        const localDate = new Date(String(date).replace(/ GMT$/, ""));
        if (Number.isNaN(localDate.getTime())) return "";

        const day = String(localDate.getDate()).padStart(2, "0");
        const month = localDate.toLocaleString("en-AU", { month: "short" });
        const year = localDate.getFullYear();
        const hours = String(localDate.getHours()).padStart(2, "0");
        const minutes = String(localDate.getMinutes()).padStart(2, "0");

        return `${day} ${month}, ${year} ${hours}:${minutes}`;
    };

    const handleDrop = (acceptedFiles) => {
        if (!acceptedFiles?.length) return;
        const file = acceptedFiles[0];

        const ext = (file.name || "").toLowerCase();
        if (!(ext.endsWith(".csv") || ext.endsWith(".xlsx"))) {
            toast.error("Only CSV or Excel (.xlsx) files are allowed.");
            return;
        }

        setUploadFile(file);
        const reader = new FileReader();
        reader.onload = () => {
            try {
                if (ext.endsWith(".csv")) {
                    Papa.parse(reader.result, {
                        header: true,
                        complete: (results) => {
                            const rows = Array.isArray(results?.data) ? results.data : [];
                            setUploadRows(rows);
                        },
                        error: () => toast.error("Failed to parse CSV file."),
                    });
                } else {
                    const wb = XLSX.read(reader.result, { type: "binary" });
                    const sheet = wb.Sheets[wb.SheetNames[0]];
                    const rows = XLSX.utils.sheet_to_json(sheet);
                    setUploadRows(Array.isArray(rows) ? rows : []);
                }
            } catch (e) {
                console.error(e);
                toast.error("Failed to parse upload file.");
            }
        };

        if (ext.endsWith(".csv")) reader.readAsText(file);
        else reader.readAsBinaryString(file);
    };

    const { getRootProps, getInputProps } = useDropzone({
        onDrop: handleDrop,
        accept: {
            "text/csv": [".csv"],
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
        },
        maxFiles: 1,
    });

    const previewDomains = useMemo(() => {
        return uploadRows
            .map((r) => String(r?.blacklist_domain || "").trim())
            .filter(Boolean)
            .slice(0, 20);
    }, [uploadRows]);

    const handleBulkUpload = async () => {
        if (!uploadFile) {
            toast.warn("Please select a CSV/Excel file first.");
            return;
        }

        if (!uploadRows.length) {
            toast.warn("No rows parsed from file.");
            return;
        }

        const hasColumn = Object.keys(uploadRows[0] || {}).includes("blacklist_domain");
        if (!hasColumn) {
            toast.error('Missing required column: "blacklist_domain".');
            return;
        }

        const formData = new FormData();
        formData.append("file", uploadFile);

        setIsUploading(true);
        try {
            const { data } = await axiosInstance.post("/blacklist/blacklists/upload", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                `Upload complete. Inserted: ${data?.inserted || 0}, duplicates: ${data?.duplicates || 0}`
            );
            setUploadFile(null);
            setUploadRows([]);
            await loadItems();
        } catch (err) {
            toast.error(err.response?.data?.error || "Bulk upload failed.");
        } finally {
            setIsUploading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const ws = XLSX.utils.json_to_sheet([{ blacklist_domain: "example.com" }], {
            header: ["blacklist_domain"],
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Blacklist Template");
        XLSX.writeFile(wb, "blacklist_template.xlsx");
    };

    return (
        <div className="min-h-screen bg-slate-50 px-3 sm:px-4 lg:px-6 py-6">
            <div className="mx-auto w-full max-w-7xl">
                <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-6 shadow-sm">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <div>
                            <h1 className="text-2xl font-extrabold text-slate-900">Blacklist</h1>
                            <p className="text-sm text-slate-600 mt-1">
                                Manage blocked domains used by the platform.
                            </p>
                        </div>

                        <button
                            type="button"
                            onClick={loadItems}
                            disabled={isLoading}
                            className="inline-flex items-center justify-center rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isLoading ? "Refreshing..." : "Refresh"}
                        </button>
                    </div>

                    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                            Create
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                            <input
                                value={newDomain}
                                onChange={(e) => setNewDomain(e.target.value)}
                                placeholder="example.com"
                                className="h-11 flex-1 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/40 focus:border-slate-300"
                            />

                            <button
                                type="button"
                                onClick={handleCreate}
                                disabled={isSaving}
                                className="h-11 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Create
                            </button>
                        </div>
                    </div>

                    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:p-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                            Bulk Upload
                        </div>
                        <p className="mb-3 text-xs text-slate-600">
                            Upload CSV or Excel with required column: <span className="font-semibold">blacklist_domain</span>
                        </p>
                        <div className="mb-3">
                            <button
                                type="button"
                                onClick={handleDownloadTemplate}
                                className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                            >
                                Download Template
                            </button>
                        </div>

                        <div
                            {...getRootProps()}
                            className={`cursor-pointer rounded-xl border-2 border-dashed p-5 text-center text-sm transition ${uploadFile
                                ? "border-slate-900 bg-white"
                                : "border-slate-300 bg-white hover:border-slate-500"
                                }`}
                        >
                            <input {...getInputProps()} />
                            <p className="text-slate-600">
                                Drag & drop a CSV/XLSX file here, or click to choose.
                            </p>
                            {uploadFile && (
                                <p className="mt-2 font-semibold text-slate-900">
                                    Selected: {uploadFile.name}
                                </p>
                            )}
                        </div>

                        {uploadRows.length > 0 && (
                            <div className="mt-3 rounded-lg border border-slate-200 bg-white p-3">
                                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
                                    Preview (first {previewDomains.length})
                                </div>
                                {previewDomains.length ? (
                                    <ul className="max-h-36 overflow-auto space-y-1 text-sm text-slate-700">
                                        {previewDomains.map((d, i) => (
                                            <li key={`${d}-${i}`} className="break-all">{d}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <div className="text-sm text-rose-600">
                                        No values found in column "blacklist_domain".
                                    </div>
                                )}
                            </div>
                        )}

                        <div className="mt-3 flex justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setUploadFile(null);
                                    setUploadRows([]);
                                }}
                                disabled={isUploading}
                                className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                            >
                                Clear
                            </button>
                            <button
                                type="button"
                                onClick={handleBulkUpload}
                                disabled={isUploading || !uploadFile}
                                className="h-10 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                            >
                                {isUploading ? "Uploading..." : "Upload"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        <div className="text-sm font-semibold text-slate-700">{rowCountLabel}</div>
                        <input
                            value={searchDomain}
                            onChange={(e) => setSearchDomain(e.target.value)}
                            placeholder="Search domain..."
                            className="h-10 w-full sm:w-72 rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/40"
                        />
                    </div>

                    <div className="mt-3 overflow-x-auto rounded-xl border border-slate-200">
                        <table className="min-w-full text-sm">
                            <thead className="bg-slate-100">
                                <tr>
                                    <th className="w-full px-4 py-3 text-left font-semibold text-slate-700">
                                        Domain
                                    </th>
                                    <th className="min-w-[170px] whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                                        Created At
                                    </th>
                                    <th className="min-w-[160px] whitespace-nowrap px-4 py-3 text-left font-semibold text-slate-700">
                                        Actions
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {filteredItems.map((item) => {
                                    const isEditing = editingId === item.blacklist_id;

                                    return (
                                        <tr key={item.blacklist_id} className="border-t border-slate-200">
                                            <td className="px-4 py-3 align-middle text-slate-900">
                                                {isEditing ? (
                                                    <input
                                                        value={editingDomain}
                                                        onChange={(e) => setEditingDomain(e.target.value)}
                                                        className="h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-400/40"
                                                    />
                                                ) : (
                                                    item.domain
                                                )}
                                            </td>

                                            <td className="min-w-[170px] whitespace-nowrap px-4 py-3 align-middle text-slate-600">
                                                {formatDate(item.created_at)}
                                            </td>

                                            <td className="w-[180px] whitespace-nowrap px-4 py-3 align-middle text-left">
                                                <div className="flex items-center justify-start gap-2">
                                                    {isEditing ? (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={handleUpdate}
                                                                disabled={isSaving}
                                                                className="rounded-lg bg-amber-600 px-3 py-2 text-xs font-semibold text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                Save
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={cancelEdit}
                                                                className="rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
                                                            >
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button
                                                                type="button"
                                                                onClick={() => startEdit(item)}
                                                                className="rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
                                                            >
                                                                Edit
                                                            </button>

                                                            <button
                                                                type="button"
                                                                onClick={() => openDeleteDialog(item)}
                                                                disabled={isSaving}
                                                                className="rounded-lg bg-rose-600 px-3 py-2 text-xs font-semibold text-white hover:bg-rose-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                Delete
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}

                                {!filteredItems.length && (
                                    <tr>
                                        <td colSpan={3} className="px-4 py-8 text-center text-sm text-slate-500">
                                            {isLoading ? "Loading..." : "No matching blacklist domains."}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </section>
            </div>

            {deleteTarget && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-xs"
                    onMouseDown={closeDeleteDialog}
                >
                    <div
                        className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl"
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-start gap-3">
                            <div>
                                <h2 className="text-lg font-extrabold text-slate-900">
                                    Delete blacklist domain?
                                </h2>

                                <p className="mt-1 text-sm text-slate-600">
                                    This action will remove the selected domain.
                                </p>
                            </div>
                        </div>

                        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                Domain
                            </div>
                            <div className="mt-1 break-all text-sm font-semibold text-slate-900">
                                {deleteTarget.domain}
                            </div>
                        </div>

                        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                            <button
                                type="button"
                                onClick={closeDeleteDialog}
                                disabled={isSaving}
                                className="h-10 rounded-xl border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                onClick={confirmDelete}
                                disabled={isSaving}
                                className="h-10 rounded-xl bg-red-700 px-4 text-sm font-semibold text-white hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSaving ? "Deleting..." : "Delete"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Blacklist;
